/**
 * Hooks de responsive compartidos.
 *
 * useIsMobile()  -> boolean, true cuando el viewport es < MOBILE_MAX (768).
 * useBreakpoint() -> 'xs' | 'sm' | 'md' | 'lg' | 'xl'.
 *
 * Estos hooks son la pieza que permite hacer responsive las pantallas
 * inline-styled (que no aceptan media queries): la pantalla hace
 *   const isMobile = useIsMobile();
 * y alterna objetos de estilo.
 */

import { useState, useEffect } from "react";
import { BREAKPOINTS, MOBILE_MAX } from "../utils/breakpoints";

const getWidth = () =>
  typeof window === "undefined" ? MOBILE_MAX + 1 : window.innerWidth;

/**
 * @param {number} [max=MOBILE_MAX] ancho máximo (exclusivo) para considerarse mobile.
 * @returns {boolean}
 */
export function useIsMobile(max = MOBILE_MAX) {
  const [isMobile, setIsMobile] = useState(() => getWidth() < max);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width:${max - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    // Safari <14 usa addListener/removeListener
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [max]);

  return isMobile;
}

function resolveBreakpoint(w) {
  if (w < BREAKPOINTS.xs) return "xs";
  if (w < BREAKPOINTS.sm) return "sm";
  if (w < BREAKPOINTS.md) return "md";
  if (w < BREAKPOINTS.lg) return "lg";
  return "xl";
}

/**
 * @returns {'xs'|'sm'|'md'|'lg'|'xl'}
 */
export function useBreakpoint() {
  const [bp, setBp] = useState(() => resolveBreakpoint(getWidth()));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setBp(resolveBreakpoint(getWidth()));
    onResize();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return bp;
}

export default useIsMobile;
